/**
 * A cache that will expire items after a given number of milliseconds (default 5 minutes).
 */
export class TimedCache<T> {
	private _cache: Lookup<{ expiry: number, item: T }> = {}

	private _size = 0
	private stopClearCycle: () => void = () => { }

	destroyed = false

	get size() { return this._size }

	constructor(private expiryInMilliseconds: number = 300000) { }

	put(key: string, item: T): void {
		// First item, start watching for expired items.
		if (this._size === 0) {
			this.startClearCycle()
		}

		if (!(key in this._cache)) {
			++this._size
		}

		const expiry = new Date().valueOf() + this.expiryInMilliseconds
		this._cache[key] = { item, expiry }
	}

	get(key: string): T | undefined {
		const cached = this._cache[key]

		if (!cached) return
		if (cached.expiry < new Date().valueOf()) {
			this.delete(key)
			return
		}
		return cached.item
	}

	delete(key: string): boolean {
		if (key in this._cache) {
			delete this._cache[key]
			if (--this._size === 0) {
				this.stopClearCycle()
			}
			return true
		}
		return false
	}

	destroy(): void {
		if (this.destroyed) return
		this.destroyed = true
		this.stopClearCycle()
		this._cache = null
	}

	private startClearCycle(): void {
		// Every 10 seconds we flush expired items.
		const id = setInterval(() => this.clearExpiredItems(), 10000)

		this.stopClearCycle = () => clearInterval(id)
	}

	private clearExpiredItems(): void {
		for (const key in this._cache) {
			if (this._cache[key].expiry < new Date().valueOf()) {
				delete this._cache[key]
				--this._size
			}
		}
		if (this._size === 0) {
			this.stopClearCycle()
		}
	}
}